import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../core/config/config.service';
import { Cliente } from '../../models/cadastro.model';

export interface MarmorariaAgendamento {
  id: number;
  tipoAgendamento: string;
  dataAgendamento: string;
  cliente: Cliente;
  enderecoAgendamento: string;
  telefoneContato: string;
  complexidade: string;
  dataConclusao: string | null;
  descricao: string;
  idOrcamento: number | null;
  periodoVisita: string | null;
  nomeRazaoSocial: string;
  idFuncionario: number | null;
}

export type MarmorariaAgendamentoPayload = MarmorariaAgendamento;

/**
 * Registro presencial (check-in/check-out) associado a um agendamento.
 * `tipoRegistro` tem tamanho máximo 1 no banco — convenção da aplicação:
 *  'E' = Entrada (início da visita) · 'S' = Saída (encerramento da visita).
 */
export interface AgendamentoRealizado {
  id?: number;
  idAgendamento: number;
  idFuncionario: number;
  timestampCheckin: string;
  latitude: number;
  longitude: number;
  descricao?: string;
  tipoRegistro: string;
}

@Injectable({
  providedIn: 'root'
})
export class MarmorariaService {
  private readonly configService = inject(ConfigService);

  constructor(private http: HttpClient) {}

  getAgendamentos(): Observable<MarmorariaAgendamento[]> {
    return this.http.get<MarmorariaAgendamento[]>(`${this.marmorariaUrl}/agendamentos`);
  }

  getAgendamentoById(id: number): Observable<MarmorariaAgendamento> {
    return this.http.get<MarmorariaAgendamento>(`${this.marmorariaUrl}/agendamentos/${id}`);
  }

  createAgendamento(payload: MarmorariaAgendamentoPayload): Observable<MarmorariaAgendamento> {
    return this.http.post<MarmorariaAgendamento>(`${this.marmorariaUrl}/agendamentos`, payload);
  }

  updateAgendamento(payload: MarmorariaAgendamentoPayload): Observable<MarmorariaAgendamento> {
    return this.http.put<MarmorariaAgendamento>(`${this.marmorariaUrl}/agendamentos/${payload.id}`, payload);
  }

  deleteAgendamento(id: number): Observable<void> {
    return this.http.delete<void>(`${this.marmorariaUrl}/agendamentos/${id}`);
  }

  getAgendamentosPorPeriodo(dataInicio: string, dataFim: string): Observable<MarmorariaAgendamento[]> {
    return this.http.get<MarmorariaAgendamento[]>(
      `${this.marmorariaUrl}/agendamentos/periodo?inicio=${encodeURIComponent(dataInicio)}&fim=${encodeURIComponent(dataFim)}`
    );
  }

  /** Agenda do dia de um funcionário (data no formato ISO `yyyy-MM-dd`). */
  getAgendamentosPorFuncionario(idFuncionario: number, data: string): Observable<MarmorariaAgendamento[]> {
    return this.http.get<MarmorariaAgendamento[]>(
      `${this.marmorariaUrl}/agendamentos/funcionario/${idFuncionario}?data=${encodeURIComponent(data)}`
    );
  }

  /** Histórico de registros presenciais, filtrável por agendamento ou funcionário. */
  getAgendamentosRealizados(filtro?: { idAgendamento?: number; idFuncionario?: number }): Observable<AgendamentoRealizado[]> {
    const params: string[] = [];
    if (filtro?.idAgendamento != null) {
      params.push(`idAgendamento=${filtro.idAgendamento}`);
    } else if (filtro?.idFuncionario != null) {
      params.push(`idFuncionario=${filtro.idFuncionario}`);
    }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<AgendamentoRealizado[]>(`${this.marmorariaUrl}/agendamentos-realizados${query}`);
  }

  /** Cria um registro presencial (check-in/check-out) com posição GPS. */
  createAgendamentoRealizado(payload: AgendamentoRealizado): Observable<AgendamentoRealizado> {
    return this.http.post<AgendamentoRealizado>(`${this.marmorariaUrl}/agendamentos-realizados`, payload);
  }

  private get marmorariaUrl(): string {
    const baseUrl = this.configService.settings()?.apiUrl;
    return `${baseUrl}`;
  }
}
